import { useState, useEffect } from 'react';
import { ToggleSwitch } from '@/components/ui/toggle-switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUpdateFaxToEmail } from '../../hooks';
import { Mail, TriangleAlert, Info, ArrowDownToLine, ArrowUpFromLine, FileText } from 'lucide-react';
import type { DID } from '../../types';

interface FaxToEmailTabProps {
  did?: DID | null;
}

export function FaxToEmailTab({ did }: FaxToEmailTabProps) {
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const updateFaxToEmail = useUpdateFaxToEmail();

  useEffect(() => {
    if (did) {
      setEnabled(!!did.has_fax_to_email);
      setEmail(did.fax_to_email_address || '');
    }
  }, [did]);

  const handleSubmit = () => {
    if (!did) return;
    updateFaxToEmail.mutate({
      tn: did.tn,
      enabled,
      email: enabled ? email : undefined,
    });
  };

  // Simple email validation
  const isValidEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="p-3 bg-emerald-50 rounded-xl">
          <Mail className="h-6 w-6 text-emerald-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">eFax</h3>
          <p className="text-sm text-slate-500 mt-1">
            Receive incoming faxes as email attachments.
          </p>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
        <TriangleAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Mutual Exclusivity</p>
          <p className="text-sm text-amber-700">
            Only one of the following services may be enabled at a time:{' '}
            <strong>Forward, Failover, eFax, and Conference</strong>
          </p>
        </div>
      </div>

      {/* Toggle Card */}
      <div className="bg-slate-50 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-slate-900">eFax</p>
            <p className="text-sm text-slate-500">
              {enabled ? 'Faxes will be sent to email' : 'eFax is disabled'}
            </p>
          </div>
          <ToggleSwitch
            enabled={enabled}
            onChange={setEnabled}
          />
        </div>

        {enabled && (
          <div className="pt-4 border-t border-slate-200">
            <Input
              type="email"
              label="Email Address"
              placeholder="Enter email to receive faxes"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              hint="Incoming faxes will be delivered to this email as PDF attachments"
              error={email && !isValidEmail(email) ? 'Please enter a valid email address' : undefined}
            />
          </div>
        )}
      </div>

      {/* How eFax Works Tutorial */}
      {enabled && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-600" />
            <h4 className="text-sm font-semibold text-indigo-900">How eFax Works</h4>
          </div>

          {/* Inbound */}
          <div className="flex gap-3">
            <ArrowDownToLine className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-indigo-900">Receiving Faxes (Inbound)</p>
              <p className="text-sm text-indigo-700 mt-1">
                Faxes received on this number will be forwarded as PDF attachments to the email address configured above.
              </p>
            </div>
          </div>

          {/* Outbound */}
          <div className="flex gap-3">
            <ArrowUpFromLine className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-indigo-900">Sending Faxes (Outbound)</p>
              <p className="text-sm text-indigo-700 mt-1">
                To send a fax, you <strong>must</strong> send from the email address configured above.
                Email your attachment to:
              </p>
              <div className="mt-2 bg-white rounded-lg border border-indigo-200 px-3 py-2">
                <code className="text-sm font-mono text-indigo-800">
                  fax<span className="text-indigo-400">##########</span>@titaniumvfax.com
                </code>
              </div>
              <p className="text-xs text-indigo-600 mt-2">
                Replace <strong>##########</strong> with the 10-digit fax number you are sending to.
              </p>
              <div className="mt-2 bg-white/60 rounded-lg border border-indigo-100 px-3 py-2">
                <p className="text-xs text-indigo-600">
                  <strong>Example:</strong>{' '}
                  <code className="font-mono">fax5559871465@titaniumvfax.com</code>{' '}
                  sends a fax to (555) 987-1465
                </p>
              </div>
            </div>
          </div>

          {/* Supported File Types */}
          <div className="flex gap-3">
            <Info className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-indigo-900">Supported Attachment Types</p>
              <div className="flex gap-2 mt-1.5">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                  .PDF
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                  .DOC
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                  .DOCX
                </span>
              </div>
              <p className="text-xs text-indigo-600 mt-1.5">
                PDF is recommended for the best results and highest success rate.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info note */}
      <div className="bg-slate-100 rounded-lg p-3 flex gap-2">
        <Info className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">
          Keep in mind that line changes can take a few minutes to propagate through our system.
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-2">
        <Button
          onClick={handleSubmit}
          isLoading={updateFaxToEmail.isPending}
          disabled={!did || (enabled && !isValidEmail(email))}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
}
